import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { ApiService } from '@services/api/api.service';
import { ProjectService } from '@services/api/project.service';
import { firstValueFrom, of, timeout } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** A stalled check would leave the router hanging with the global loader on screen. */
const VISIBILITY_CHECK_TIMEOUT_MS = 4000;

/**
 * Blocks `/projects/edit_/:id/**` and `/projects/:projectId/calendar` for users the project's
 * Project Visibility setting leaves out, so a hidden project can't be opened by typing its URL.
 *
 * Denies only on an explicit "no" from the backend (`can_view: false` or a 403). Anything else —
 * route not deployed yet, network error, timeout — lets the user through, because the backend's
 * own filtering on the project endpoints is the real enforcement and a broken check must not lock
 * everyone out of every project. See docs/backend/projects/project-access-visibility.md.
 */
export const projectVisibilityGuard: CanActivateFn = async route => {
  const api = inject(ApiService);
  const router = inject(Router);
  const projectService = inject(ProjectService);

  if ((api.getRole() ?? '').toLowerCase() === 'master admin') {
    return true;
  }

  const projectId = findProjectId(route);
  if (!projectId) {
    return true;
  }

  const response: any = await firstValueFrom(
    projectService.canViewProject(projectId).pipe(
      timeout(VISIBILITY_CHECK_TIMEOUT_MS),
      catchError(error => of(error?.status === 403 ? { data: { can_view: false } } : null))
    ),
    // ApiService.get completes without emitting when the session isn't ready yet.
    { defaultValue: null }
  );

  return response?.data?.can_view === false ? router.parseUrl('/access-denied') : true;
};

function findProjectId(route: ActivatedRouteSnapshot): string | null {
  return route.pathFromRoot.reduce<string | null>((id, snapshot) => snapshot.paramMap.get('id') ?? snapshot.paramMap.get('projectId') ?? id, null);
}
